import { predictionAbi } from '../abis/prediction';
import { encodeFunctionData, PublicClient, Address } from 'viem';

export const getOptions = async (address: Address, client: PublicClient) => {
  return await client.readContract({
    address,
    abi: predictionAbi,
    functionName: 'options',
  });
};

export type GetOptionOutAmountProps = {
  address: Address; // contract address
  optionIndex: bigint; // optionsOutIndex
  tokenAmount: bigint; // baseToken amount
};

export const getOptionOutAmount = async (
  { address, optionIndex, tokenAmount }: GetOptionOutAmountProps,
  client: PublicClient
) => {
  return await client.readContract({
    address,
    abi: predictionAbi,
    functionName: 'getAmountOut',
    args: [optionIndex, tokenAmount],
  });
};

export type GetTokenOutAmountProps = {
  address: Address; // contract address
  optionsLength?: number; // options length
  optionIndex: bigint; // option Out Index
  optionAmount: bigint; // option Amount
};

export const getTokenOutAmount = async (
  { address, optionsLength, optionIndex, optionAmount }: GetTokenOutAmountProps,
  client: PublicClient
) => {
  const len =
    optionsLength ?? Number(await client.readContract({ address, abi: predictionAbi, functionName: 'MAX_OPTION' }));
  const optionsAmounts: bigint[] = new Array(len).fill(BigInt(0));
  optionsAmounts[Number(optionIndex)] = optionAmount;
  return await client.readContract({
    address,
    abi: predictionAbi,
    functionName: 'getAmountsOut',
    args: [optionsAmounts],
  });
};

export type EncodeDepositProps = {
  optionIndex: bigint; // out option Index
  tokenAmount: bigint; // base token amount
  minOptionAmount: bigint; // min option Amount
  deadline: bigint; // second time
};

export const encodeDeposit = ({ optionIndex, tokenAmount, minOptionAmount, deadline }: EncodeDepositProps) => {
  return encodeFunctionData({
    abi: predictionAbi,
    functionName: 'deposit',
    args: [optionIndex, tokenAmount, minOptionAmount, deadline],
  });
};

export type EncodeWithdrawProps = {
  optionIndex: bigint; // in option Index
  optionAmount: bigint; // option Amount
  minTokenAmount: bigint; // min base token amount
  deadline: bigint; // second time
};

export const encodeWithdraw = ({ optionIndex, optionAmount, minTokenAmount, deadline }: EncodeWithdrawProps) => {
  return encodeFunctionData({
    abi: predictionAbi,
    functionName: 'withdraw',
    args: [optionIndex, optionAmount, minTokenAmount, deadline],
  });
};

export const encodeClaimAll = () => {
  return encodeFunctionData({
    abi: predictionAbi,
    functionName: 'claimAll',
  });
};

// CTF ConditionalTokens.redeemPositions ABI (for new contract deployment)
const redeemPositionsAbi = [
  {
    type: 'function',
    name: 'redeemPositions',
    inputs: [
      { name: 'collateralToken', type: 'address', internalType: 'contract IERC20' },
      { name: 'parentCollectionId', type: 'bytes32' },
      { name: 'conditionId', type: 'bytes32' },
      { name: 'indexSets', type: 'uint256[]' },
    ],
    outputs: [],
    stateMutability: 'nonpayable',
  },
] as const;

export type EncodeRedeemPositionsProps = {
  collateralToken: Address;
  conditionId: `0x${string}`;
  outcomeSlotCount: number;
};

export const encodeRedeemPositions = ({ collateralToken, conditionId, outcomeSlotCount }: EncodeRedeemPositionsProps) => {
  // Build index sets: [1, 2] for binary, [1, 2, 4] for 3-outcome, etc.
  const indexSets = Array.from({ length: outcomeSlotCount }, (_, i) => BigInt(1 << i));
  return encodeFunctionData({
    abi: redeemPositionsAbi,
    functionName: 'redeemPositions',
    args: [
      collateralToken,
      '0x0000000000000000000000000000000000000000000000000000000000000000' as `0x${string}`,
      conditionId,
      indexSets,
    ],
  });
};
